import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import GoogleSignInButton from '../GoogleSignInButton';
import AppleSignInButton from '../AppleSignInButton';
import { authService } from '../../services';
import { getStaySignedIn, setStaySignedIn } from '../../services/staySignedIn';
import useMediaQuery from '../../utils/useMediaQuery';
import stanchions from '../../assets/img/stanchions-disco-sm.png';
import { Pill, BackPill, RingCircle, Segmented, SpinningO, C } from '../portalkit';
import AuthField, { PhoneField } from './AuthField';
import { normalizeInviteCode, readInvite } from '../../utils/inviteRef';
import { onboardingHref } from '../../utils/returnTo';

/**
 * The auth screens — Login · Forgot password · Register (· Reset by emailed link), one component
 * (design_handoff_auth_profile_planner §1–3, Sept 27 2026). A full-height black page with the
 * radial glow, a back pill and the spinning portal up top, the stanchions, then one #111 card.
 * The screens switch in place: they are routes, so the SPA swaps them without a page load.
 *
 * Wired to the existing auth service: login, register, Google / Apple, the emailed reset link and
 * the SMS code (a number verified in Settings is the one recovery path that survives a lost inbox).
 *
 * Copy is the handoff's, with one deliberate departure: registration signs the person straight in
 * (the server issues tokens; there is no confirmation email), so the "we sent a confirmation link"
 * done state would be a lie and is not shown. The account lands where it was headed.
 */

const EMAIL_OK = /\S+@\S+\.\S+/;
const CREDENTIALS_COPY = 'THAT EMAIL AND PASSWORD DON’T MATCH. TRY AGAIN OR RESET IT.';
const looksLikeBadCredentials = (msg = '') => /invalid|incorrect|wrong|credential|password|not found|unauthori[sz]ed/i.test(msg);

function ErrorBanner({ children }) {
  if (!children) return null;
  return (
    <div role="alert" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', borderRadius: 16, background: C.errorBg, color: C.pastelPink, fontSize: 11, fontWeight: 700, letterSpacing: '.1em', lineHeight: 1.5, textTransform: 'uppercase' }}>
      <span aria-hidden="true" style={{ flexShrink: 0, width: 8, height: 8, marginTop: 4, borderRadius: '50%', background: C.pink }} />
      {children}
    </div>
  );
}

/**
 * STAY SIGNED IN — on by default, and off is a deliberate choice for a borrowed
 * device. On, the server gives the session the long sliding refresh window, so the
 * person is never signed out while they keep using the app. The answer is stored per
 * device (services/staySignedIn) rather than held in this form, because Google and
 * Apple sign-in leave the page and come back.
 */
function StaySignedIn({ checked, onChange, disabled }) {
  return (
    <label
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ position: 'absolute', width: 1, height: 1, margin: -1, padding: 0, border: 0, clip: 'rect(0 0 0 0)', overflow: 'hidden', whiteSpace: 'nowrap' }}
      />
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 20, height: 20, marginTop: 1, borderRadius: 7,
          background: checked ? C.pink : 'transparent',
          boxShadow: `inset 0 0 0 ${checked ? 0 : 1.5}px #333`,
          transition: 'background 140ms ease-out',
        }}
      >
        {checked && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12.5 L9.5 18 L20 6.5" />
          </svg>
        )}
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}>Stay signed in</span>
        <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.08em', lineHeight: 1.5, color: C.onBlack2, textTransform: 'uppercase' }}>
          {checked
            ? 'We won’t ask again on this device.'
            : 'Turn this on to stop signing out. Leave it off on a shared device.'}
        </span>
      </span>
    </label>
  );
}

function Intro({ children }) {
  return <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: C.onBlack3, textWrap: 'pretty' }}>{children}</p>;
}

/** The shared done / success block: ring circle + check, 15px title, 13px body, rises in. */
export function DoneBlock({ title, children }) {
  return (
    <div className="pk-rise" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '8px 0 4px', textAlign: 'center' }}>
      <RingCircle />
      <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}>{title}</span>
      <span style={{ fontSize: 13, lineHeight: 1.6, color: C.onBlack3, textWrap: 'pretty' }}>{children}</span>
    </div>
  );
}

function OrDivider() {
  return (
    <div aria-hidden="true" style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 10, fontWeight: 700, letterSpacing: '.2em', color: C.onBlack4 }}>
      <span style={{ flex: 1, height: 1, background: '#262626' }} />OR<span style={{ flex: 1, height: 1, background: '#262626' }} />
    </div>
  );
}

export default function AuthScreen({ screen = 'login' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const phone = useMediaQuery('(max-width: 639px)');

  // Where to land afterwards: ?redirect= (ProtectedRoute, cross-links) or router state. Kept through
  // the login ↔ register hop so creating an account lands the person where they started.
  const redirectParam = searchParams.get('redirect') || location.state?.from?.pathname || '';
  // An invite card (heyportal.app/i/{code}): `?ref=` says this sign-in / sign-up came from one,
  // and the landing it came from remembered the inviter's page — that is where they land.
  const refParam = normalizeInviteCode(searchParams.get('ref'));
  const invitePath = refParam ? (readInvite()?.path || '') : '';
  const from = redirectParam || invitePath || '/square';
  const withRedirect = (path) => {
    const q = new URLSearchParams();
    if (redirectParam) q.set('redirect', redirectParam);
    if (refParam) q.set('ref', refParam);
    const qs = q.toString();
    return `${path}${qs ? `?${qs}` : ''}`;
  };
  const token = searchParams.get('token');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Their last answer on this device is the default, so it is not a decision to retake.
  const [stay, setStay] = useState(getStaySignedIn);
  const [confirm, setConfirm] = useState('');
  const [country, setCountry] = useState('CA');
  const [number, setNumber] = useState('');
  const [code, setCode] = useState('');
  const [newPw, setNewPw] = useState('');
  const [method, setMethod] = useState('email');   // forgot: email | phone
  const [step, setStep] = useState('form');        // form | sent | code | reset | done
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const emailOk = EMAIL_OK.test(email);
  const digits = number.replace(/\D/g, '');
  const phoneOk = digits.length >= 10;
  const clearErr = () => { if (err) setErr(null); };
  const set = (setter) => (e) => { setter(e.target.value); clearErr(); };

  const run = async (call, onOk) => {
    setLoading(true); setErr(null);
    try {
      const r = await call();
      if (r?.success) onOk(r);
      else setErr(r?.error || 'Something went wrong. Try again.');
    } catch (e) {
      setErr('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* ── per-screen ─────────────────────────────────────────────────────── */
  let v = { title: '', subtitle: '', backLabel: 'BACK', backTo: '/', showGoogle: false, tabs: false, intro: null, fields: null, extras: null, done: null, primary: null, ready: false, submit: null, secondary: null, footQ: '', footA: '', footTo: '/login' };

  if (screen === 'login') {
    v = {
      ...v, backTo: '/', showGoogle: true,
      footQ: 'NEW TO THE PARTY?', footA: 'SIGN UP', footTo: withRedirect('/register'),
      fields: (
        <>
          <AuthField id="email" label="EMAIL" required type="email" inputMode="email" autoComplete="email" value={email} onChange={set(setEmail)} placeholder="your@email.com" disabled={loading} />
          <AuthField id="password" label="PASSWORD" required password autoComplete="current-password" value={password} onChange={set(setPassword)} placeholder="Enter your password" disabled={loading} />
        </>
      ),
      extras: <StaySignedIn checked={stay} onChange={(on) => { setStay(on); setStaySignedIn(on); }} disabled={loading} />,
      primary: 'LOGIN', ready: emailOk && password.length > 0,
      submit: () => run(() => authService.login(email, password), () => { window.location.href = from; }),
      secondary: { label: 'FORGOT PASSWORD?', to: '/forgot-password' },
    };
    if (err && looksLikeBadCredentials(err)) v.errText = CREDENTIALS_COPY;
  } else if (screen === 'register') {
    const short = password.length > 0 && password.length < 8;
    const mismatch = confirm.length > 0 && confirm !== password;
    v = {
      ...v, title: 'JOIN THE PARTY', subtitle: 'CREATE YOUR CREDENTIAL', backTo: withRedirect('/register'), showGoogle: true,
      footQ: 'ALREADY HAVE CREDENTIALS?', footA: 'LOGIN', footTo: withRedirect('/login'),
      fields: (
        <>
          <AuthField id="email" label="EMAIL" required type="email" inputMode="email" autoComplete="email" value={email} onChange={set(setEmail)} placeholder="your@email.com" disabled={loading} />
          <PhoneField id="phone" country={country} number={number} onChange={({ country: c, number: n }) => { setCountry(c); setNumber(n); clearErr(); }} hint="OPTIONAL — LETS YOU RESET YOUR PASSWORD BY TEXT IF YOU LOSE YOUR EMAIL" disabled={loading} />
          <AuthField id="password" label="PASSWORD" required password autoComplete="new-password" value={password} onChange={set(setPassword)} placeholder="Min. 8 characters" hint={short ? `${8 - password.length} MORE CHARACTER${8 - password.length === 1 ? '' : 'S'}` : ''} hintError edgeError={short} disabled={loading} />
          <AuthField id="confirmPassword" label="CONFIRM PASSWORD" required password autoComplete="new-password" value={confirm} onChange={set(setConfirm)} placeholder="Re-enter your password" hint={mismatch ? 'DOESN’T MATCH YET' : ''} hintError edgeError={mismatch} disabled={loading} />
        </>
      ),
      extras: <StaySignedIn checked={stay} onChange={(on) => { setStay(on); setStaySignedIn(on); }} disabled={loading} />,
      primary: 'CREATE ACCOUNT', ready: emailOk && password.length >= 8 && confirm === password,
      // Sign-up v3: a new account walks the people-first flow first, then lands where it was headed.
      submit: () => run(
        () => authService.register(email, password, number.trim() || null, number.trim() ? country : null),
        () => { window.location.href = onboardingHref(from); },
      ),
    };
  } else if (screen === 'forgot') {
    v = { ...v, title: 'LOST ACCESS?', subtitle: 'RESET YOUR CREDENTIALS', backLabel: 'BACK TO LOGIN', backTo: '/login', footQ: 'REMEMBER YOUR PASSWORD?', footA: 'LOGIN', footTo: '/login' };
    const toLogin = () => navigate('/login');
    if (step === 'form') {
      v = {
        ...v, tabs: true,
        intro: method === 'email'
          ? 'Enter your email address and we\'ll send you a link to reset your password.'
          : 'Enter the phone number on your account and we\'ll text you a code. It has to be a number you verified in Settings.',
        fields: method === 'email'
          ? <AuthField id="email" label="EMAIL" required type="email" inputMode="email" autoComplete="email" value={email} onChange={set(setEmail)} placeholder="your@email.com" disabled={loading} />
          : <PhoneField id="phone" required country={country} number={number} onChange={({ country: c, number: n }) => { setCountry(c); setNumber(n); clearErr(); }} disabled={loading} />,
        primary: method === 'email' ? 'SEND RESET LINK' : 'TEXT ME A CODE',
        ready: method === 'email' ? emailOk : phoneOk,
        submit: method === 'email'
          ? () => run(() => authService.forgotPassword(email), () => setStep('sent'))
          // Advances even when the number isn't on an account — the next screen is identical
          // either way, so this form can't be used to test which numbers are registered.
          : () => run(() => authService.forgotPasswordByPhone(number.trim(), country), () => { setCode(''); setStep('code'); }),
      };
    } else if (step === 'sent') {
      v = {
        ...v,
        done: <DoneBlock title="CHECK YOUR EMAIL.">If {email || 'that address'} has an account, a reset link is on its way. It expires in 1 hour.</DoneBlock>,
        primary: 'BACK TO LOGIN', ready: true, submit: toLogin,
        secondary: { label: 'RESEND LINK', onClick: () => run(() => authService.forgotPassword(email), () => {}) },
      };
    } else if (step === 'code') {
      v = {
        ...v,
        intro: `We texted a 6-digit code to •••• ${digits.slice(-4)}. Enter it with your new password.`,
        fields: (
          <>
            <AuthField id="otp" label="CODE" required type="text" inputMode="numeric" maxLength={6} autoComplete="one-time-code" fontSize={20} letterSpacing=".5em" value={code} onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); clearErr(); }} placeholder="000000" disabled={loading} />
            <AuthField id="newPassword" label="NEW PASSWORD" required password autoComplete="new-password" value={newPw} onChange={set(setNewPw)} placeholder="Min. 8 characters" disabled={loading} />
          </>
        ),
        primary: 'RESET PASSWORD', ready: code.length === 6 && newPw.length >= 8,
        submit: () => run(() => authService.resetPasswordByPhone(number.trim(), country, code, newPw), () => setStep('reset')),
        secondary: { label: 'RESEND CODE', onClick: () => run(() => authService.forgotPasswordByPhone(number.trim(), country), () => setCode('')) },
      };
    } else {
      v = { ...v, done: <DoneBlock title="PASSWORD RESET.">You’re set. Log in with your new password.</DoneBlock>, primary: 'LOGIN', ready: true, submit: toLogin };
    }
  } else if (screen === 'reset') {
    // The emailed link lands here with ?token=.
    const mismatch = confirm.length > 0 && confirm !== newPw;
    v = { ...v, title: 'NEW CREDENTIALS', subtitle: 'CREATE A NEW PASSWORD', backLabel: 'BACK TO LOGIN', backTo: '/login', footQ: 'REMEMBER YOUR PASSWORD?', footA: 'LOGIN', footTo: '/login' };
    if (!token) {
      v = { ...v, done: <DoneBlock title="INVALID LINK.">This password reset link is invalid or has expired.</DoneBlock>, primary: 'REQUEST NEW LINK', ready: true, submit: () => navigate('/forgot-password') };
    } else if (step === 'done') {
      v = { ...v, done: <DoneBlock title="PASSWORD RESET.">You’re set. Log in with your new password.</DoneBlock>, primary: 'LOGIN', ready: true, submit: () => navigate('/login', { replace: true }) };
    } else {
      v = {
        ...v,
        fields: (
          <>
            <AuthField id="password" label="NEW PASSWORD" required password autoComplete="new-password" value={newPw} onChange={set(setNewPw)} placeholder="Min. 8 characters" hint={newPw.length > 0 && newPw.length < 8 ? `${8 - newPw.length} MORE CHARACTER${8 - newPw.length === 1 ? '' : 'S'}` : ''} hintError edgeError={newPw.length > 0 && newPw.length < 8} disabled={loading} />
            <AuthField id="confirmPassword" label="CONFIRM PASSWORD" required password autoComplete="new-password" value={confirm} onChange={set(setConfirm)} placeholder="Re-enter your password" hint={mismatch ? 'DOESN’T MATCH YET' : ''} hintError edgeError={mismatch} disabled={loading} />
          </>
        ),
        primary: 'RESET PASSWORD', ready: newPw.length >= 8 && confirm === newPw,
        submit: () => run(() => authService.resetPassword(token, newPw), () => setStep('done')),
      };
    }
  }

  const disabled = !v.ready || loading;
  const heroW = screen === 'login' ? (phone ? 200 : 260) : (phone ? 120 : 150);
  const onSubmit = (e) => { e.preventDefault(); if (!disabled) v.submit?.(); };

  return (
    <div className="pk" style={{ position: 'relative', minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: '#000', color: '#fff', overflowX: 'clip' }}>
      {/* Glow */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '50%', top: 0, width: 900, height: 520, transform: 'translateX(-50%)', background: 'radial-gradient(closest-side, rgba(253,25,159,.14), rgba(18,123,253,.08) 55%, transparent)', pointerEvents: 'none' }} />

      {/* Top bar: back pill · spinning portal */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: phone ? 'calc(env(safe-area-inset-top) + 16px) 16px 16px' : 'calc(env(safe-area-inset-top) + 28px) 32px 28px' }}>
        <BackPill to={v.backTo}>{v.backLabel}</BackPill>
        <SpinningO size={32} />
      </div>

      <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: phone ? 24 : 32, padding: phone ? '8px 16px calc(env(safe-area-inset-bottom) + 40px)' : '8px 24px 64px', boxSizing: 'border-box' }}>
        {/* Hero */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}>
          <img src={stanchions} alt="" style={{ width: heroW, display: 'block' }} />
          {v.title && (
            <>
              <h1 style={{ margin: 0, fontSize: phone ? 30 : 44, fontWeight: 800, letterSpacing: '.08em', lineHeight: 1.05, textWrap: 'balance', textTransform: 'uppercase' }}>{v.title}</h1>
              <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.2em', color: C.onBlack2, textTransform: 'uppercase' }}>{v.subtitle}</span>
            </>
          )}
        </div>

        {/* Card */}
        <form onSubmit={onSubmit} noValidate style={{ width: '100%', maxWidth: 440, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 20, padding: phone ? '22px 18px' : 32, borderRadius: 28, background: C.cardDark, boxShadow: 'inset 0 0 0 1px #222' }}>
          <ErrorBanner>{v.errText || err}</ErrorBanner>

          {v.showGoogle && (
            <>
              {/* Apple first — its guidelines ask for it to be no less prominent. Renders on iOS / prod only. */}
              <AppleSignInButton variant="pill" redirect={from} disabled={loading} />
              <GoogleSignInButton variant="pill" redirect={from} disabled={loading} label="Continue with Google" />
              <OrDivider />
            </>
          )}

          {v.tabs && (
            <Segmented label="How to reset" tabs={[{ key: 'email', label: 'EMAIL' }, { key: 'phone', label: 'PHONE' }]} value={method} onChange={(k) => { setMethod(k); setErr(null); }} />
          )}

          {v.intro && <Intro>{v.intro}</Intro>}
          {v.fields}
          {v.extras}
          {v.done}

          {v.primary && (
            <Pill type="submit" variant="primary" on="black" size={52} disabled={disabled} style={{ marginTop: 4, width: '100%' }}>
              {loading ? 'ONE SEC…' : v.primary}
            </Pill>
          )}

          {v.secondary && (v.secondary.to ? (
            <Link to={v.secondary.to} className="pk pk-btn pk-text-on-black" style={{ alignSelf: 'center', padding: '6px 10px', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', textDecoration: 'none' }}>{v.secondary.label}</Link>
          ) : (
            <button type="button" onClick={v.secondary.onClick} disabled={loading} className="pk pk-btn pk-text-on-black" style={{ alignSelf: 'center', padding: '6px 10px', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase' }}>{v.secondary.label}</button>
          ))}
        </form>

        {/* Footer line */}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8, fontSize: 11, fontWeight: 600, letterSpacing: '.14em', color: C.onBlack2, textTransform: 'uppercase' }}>
          <span>{v.footQ}</span>
          <Link to={v.footTo} style={{ color: '#fff', fontWeight: 800, textDecoration: 'none', boxShadow: `inset 0 -2px 0 ${C.pink}` }}>{v.footA}</Link>
        </div>
      </div>
    </div>
  );
}
